/**
 * The `calendar` section — economic releases and earnings in one place
 * (`docs/plans/macro.md`; look and behaviour from `docs/mockups/macro-v2.html`
 * section 03, the spec).
 *
 * Week or Month, as a List (a row per event under each day) or a Grid
 * (weekdays as columns); Merged, Side by side (list only), Economic or
 * Earnings. ★ Key / All and ET / PT filter and relabel the economic rows.
 * A day header collapses its day, a row's ▾ opens its history, "+N more"
 * shows every matched reporter of that day, and a month cell opens its week.
 *
 * **What is stored** is the config (`CalendarSectionConfig`): range, layout,
 * view, earnings sources, top cap, per day, Group, and the jump layout.
 * Everything else — the week being viewed, Key/All, the zone, open rows,
 * collapsed days and sectors — is view state (D8) and lives here. Without
 * `onConfig` (no workspace to write to) the stored choices are this view's own
 * and are not kept, `useWidgetSetting`'s rule.
 *
 * **Group: Sector** (owner, 2026-11-01) sub-groups each time block of a week
 * under sector headers, the top 3 of each (`sliceDayBySector`); the month view
 * is never grouped. **A symbol click** (grid tile, list ticker, month logo)
 * opens a chart layout with the symbol on it (`chartJump`, App's routing): the
 * first time, or when the saved layout is gone or has no chart, it asks which.
 * Switching layout unmounts this section, so the week on screen is remembered
 * for the page in `ANCHORS` — never stored (the schema's reason).
 *
 * Data: `api/macro-calendar.ts` — earnings from `/macro/earnings`, economic
 * from `/macro/calendar/econ` (both admin only). A release's ▾ history is the
 * real past releases of that report held in the same response; none known,
 * none drawn. Logos are the screener's logo-column template, with a
 * letter tile where FMP has none.
 */
import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import type { CalendarSectionConfig } from "@qufolio/model";
import { LOGO_COLUMN, resolveImageUrl } from "@qufolio/screener-grid";
import {
  earningsConfigured,
  econConfigured,
  fetchEarnings,
  fetchEconomic,
  findEarnings,
  type EarningsDay,
  type EarningsFind,
  type EarningsPayload,
  type EarningsRow,
  type EconEvent,
  type EconPayload,
} from "../../api/macro-calendar";
import {
  addDays,
  blockOf,
  CALENDAR_SOURCES,
  dayLabel,
  dayOfMonth,
  econForDay,
  epsClass,
  estVsLast,
  dayColumns,
  formatCap,
  formatEps,
  fromWireSource,
  formatTime,
  hiddenBreakdown,
  monthLabel,
  monthOf,
  monthWeeks,
  nextKeyEvent,
  nyNow,
  resolveCalendarConfig,
  resolveJump,
  rowSources,
  shiftMonth,
  shortDayLabel,
  SECTOR_CAP,
  sliceDay,
  sliceDayBySector,
  SOURCE_LABEL,
  surpriseClass,
  toggleSource,
  toWireSource,
  viewDays,
  weekDays,
  weekLabel,
  weekStart,
  type CalendarTz,
  type DaySlice,
  type GroupedDaySlice,
  type JumpTarget,
  type SectorGroup,
  type TimeBlock,
} from "../../lib/macro-calendar";
import type { MacroSectionProps } from "./types";
import { useSparkHover } from "../SparkCard";

type Load<T> = { state: "loading" } | { state: "ok"; data: T } | { state: "error"; message: string } | { state: "off" };

/** Reporters drawn in a month cell before "+N" (the mock's `earnDay(iso, 6)`). */
const MONTH_CELL_LOGOS = 6;
/** Key releases named in a month cell before "+N more releases". */
const MONTH_CELL_RELEASES = 3;

/**
 * The week or month each calendar section was on, for the page: a symbol click
 * switches layout, which unmounts the section, and coming back should find the
 * same week. View state, so never written to the workspace.
 */
const ANCHORS = new Map<string, string>();

/** Tests only: start every section on this week again. */
export function forgetCalendarWeeks() {
  ANCHORS.clear();
}

/** A day's earnings as the view cuts them: per day, or by sector. */
type EarnSlice = DaySlice | GroupedDaySlice;
const isGrouped = (e: EarnSlice): e is GroupedDaySlice => "blocks" in e;

const BLOCK_LABEL: Record<TimeBlock, string> = {
  bmo: "Before the open",
  amc: "After the close",
  none: "Time not announced",
};

export default function CalendarSection({ section, config, onConfig, chartJump }: MacroSectionProps<"calendar">) {
  const [local, setLocal] = useState<CalendarSectionConfig>({});
  const cfg = resolveCalendarConfig(onConfig ? config : { ...config, ...local });
  const set = (patch: CalendarSectionConfig) =>
    onConfig ? onConfig({ ...config, ...patch }) : setLocal((prev) => ({ ...prev, ...patch }));

  const now = useMemo(() => nyNow(new Date()), []);
  const [anchor, setAnchor] = useState(() => ANCHORS.get(section.id) ?? weekStart(now.date));
  useEffect(() => {
    ANCHORS.set(section.id, anchor);
  }, [section.id, anchor]);
  const [hiDay, setHiDay] = useState<string | null>(null);
  const [keyOnly, setKeyOnly] = useState(true);
  const [tz, setTz] = useState<CalendarTz>("ET");
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [more, setMore] = useState<ReadonlySet<string>>(new Set());
  const [shutSec, setShutSec] = useState<ReadonlySet<string>>(new Set());
  /** The "Jump to which layout?" chooser; `symbol` is the click waiting on it. */
  const [chooser, setChooser] = useState<{ symbol?: string } | null>(null);
  const toggle = (setter: typeof setShut, key: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const { days, from, to } = viewDays(cfg.range, anchor);
  const month = monthOf(cfg.range === "month" ? anchor : weekStart(anchor));
  const wire = cfg.earningsSources.map(toWireSource);
  const wireKey = wire.join(",");

  // Find (owner, 2026-11-03): one symbol's next report, whatever the filters.
  // `shown` is the symbol Show put on the page — asked for with `include`, so
  // it is drawn even when no switched-on source holds it — and outlined.
  const [findText, setFindText] = useState("");
  const [found, setFound] = useState<Load<EarningsFind> | null>(null);
  const [shown, setShown] = useState<string | null>(null);

  const [earn, setEarn] = useState<Load<EarningsPayload>>({ state: "loading" });
  const showEarn = cfg.view !== "econ";
  useEffect(() => {
    if (!showEarn) return;
    if (!earningsConfigured) {
      setEarn({ state: "off" });
      return;
    }
    let live = true;
    setEarn({ state: "loading" });
    fetchEarnings({ from, to, sources: wire, cap: cfg.topCap, include: shown ?? undefined })
      .then((data) => live && setEarn({ state: "ok", data }))
      .catch((e: unknown) => live && setEarn({ state: "error", message: e instanceof Error ? e.message : String(e) }));
    return () => {
      live = false;
    };
    // `wire` is a fresh array each render; its joined key is the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, wireKey, cfg.topCap, showEarn, shown]);

  const [econ, setEcon] = useState<Load<EconPayload>>({ state: "loading" });
  // The server's week is Monday..Sunday of the anchor; its month runs Monday
  // before the 1st to Sunday after the last — both cover `days`.
  const econAnchor = cfg.range === "week" ? weekStart(anchor) : `${month}-01`;
  useEffect(() => {
    if (!econConfigured) {
      setEcon({ state: "off" });
      return;
    }
    let live = true;
    setEcon({ state: "loading" });
    fetchEconomic(cfg.range, econAnchor)
      .then((data) => live && setEcon({ state: "ok", data }))
      .catch((e: unknown) => live && setEcon({ state: "error", message: e instanceof Error ? e.message : String(e) }));
    return () => {
      live = false;
    };
  }, [cfg.range, econAnchor]);

  const [upcoming, setUpcoming] = useState<EconEvent | undefined>();
  useEffect(() => {
    if (!econConfigured) return;
    let live = true;
    // This month and the next: the pill looks up to three weeks ahead.
    Promise.all([fetchEconomic("month", now.date), fetchEconomic("month", shiftMonth(now.date, 1))])
      .then((ps) => live && setUpcoming(nextKeyEvent(ps.flatMap((p) => p.events), now)))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [now]);

  const spark = useSparkHover();
  const econEvents = econ.state === "ok" ? econ.data.events : [];
  const earnByDay = useMemo(() => {
    const m = new Map<string, EarningsDay>();
    if (earn.state === "ok") for (const d of earn.data.days) m.set(d.date, d);
    return m;
  }, [earn]);

  const econOf = (iso: string) => econForDay(econEvents, iso, keyOnly);
  // Group: Sector is a week's; the month grid (asked with a limit) and a month
  // list are never grouped.
  const grouped = cfg.group === "sector" && cfg.range === "week";
  const earnOf = (iso: string, limit?: number): EarnSlice =>
    grouped && limit === undefined
      ? sliceDayBySector(earnByDay.get(iso), more.has(iso))
      : sliceDay(earnByDay.get(iso), cfg.perDay, more.has(iso), limit);

  const targets = chartJump?.targets ?? [];
  const jumpTo = resolveJump(targets, cfg.jumpLayoutId);
  const pick = chartJump
    ? (symbol: string) => {
        spark.onHoverSymbol(null, null);
        if (jumpTo.kind === "go") chartJump.open(jumpTo.target.id, symbol);
        else setChooser({ symbol });
      }
    : undefined;
  const choose = (t: JumpTarget) => {
    const symbol = chooser?.symbol;
    setChooser(null);
    set({ jumpLayoutId: t.id });
    if (symbol) chartJump?.open(t.id, symbol);
  };

  const prev = () => {
    setHiDay(null);
    setAnchor((a) => (cfg.range === "week" ? addDays(weekStart(a), -7) : shiftMonth(a, -1)));
  };
  const next = () => {
    setHiDay(null);
    setAnchor((a) => (cfg.range === "week" ? addDays(weekStart(a), 7) : shiftMonth(a, 1)));
  };
  const setRange = (range: "week" | "month") => {
    setHiDay(null);
    // Into a month: the month of the week's Wednesday, so a week that straddles
    // two opens the one most of it is in. Into a week: the anchor's week.
    setAnchor((a) => (range === "month" ? `${monthOf(addDays(weekStart(a), 2))}-01` : weekStart(a)));
    set({ range });
  };
  const openWeek = (iso: string) => {
    setAnchor(weekStart(iso));
    setHiDay(iso);
    set({ range: "week" });
  };

  const runFind = () => {
    const symbol = findText.trim().toUpperCase();
    if (!symbol) {
      setFound(null);
      setShown(null);
      return;
    }
    setFound({ state: "loading" });
    findEarnings({ symbol, from: weekStart(now.date), sources: wire, cap: cfg.topCap })
      .then((data) => setFound({ state: "ok", data }))
      .catch((e: unknown) => setFound({ state: "error", message: e instanceof Error ? e.message : String(e) }));
  };
  const showFound = (f: EarningsFind) => {
    if (!f.report) return;
    setShown(f.symbol);
    if (cfg.view === "econ") set({ view: "merged" });
    openWeek(f.report.date);
  };

  // Hover a symbol → its last 60 bars, the screener's card (owner, 2026-11-01).
  // Placed off the day column (grid) or the table (list), like off the screener.
  const hover = (symbol: string | null, el: HTMLElement | null) => {
    if (!symbol || !el) return spark.onHoverSymbol(null, null);
    const box = (el.closest(".qf-cal-gc, .qf-cal-gmc, table") ?? el) as HTMLElement;
    spark.onHoverSymbol(symbol, { cell: el.getBoundingClientRect(), grid: box.getBoundingClientRect() });
  };

  const ctx: Ctx = {
    tz, open, more, shut, shutSec, hiDay, month, range: cfg.range, view: cfg.view, perDay: cfg.perDay, hover,
    econOf, earnOf, pick,
    toggleSec: (key) => toggle(setShutSec, key),
    toggleOpen: (id) => toggle(setOpen, id),
    toggleMore: (iso) => toggle(setMore, iso),
    toggleDay: (iso) => toggle(setShut, iso),
    openWeek,
  };

  let body: ReactNode;
  if (cfg.layout === "grid") {
    body = cfg.range === "week" ? <GridWeek ctx={ctx} days={days} /> : <GridMonth ctx={ctx} />;
  } else if (cfg.view === "side") {
    body = (
      <div className="qf-cal-side">
        <div>
          <div className="qf-cal-colt">Economic</div>
          <ListTable ctx={ctx} kind="econ" days={days} />
        </div>
        <div>
          <div className="qf-cal-colt">Earnings</div>
          <ListTable ctx={ctx} kind="earn" days={days} />
        </div>
      </div>
    );
  } else {
    body = <ListTable ctx={ctx} kind={cfg.view} days={days} />;
  }

  return (
    <div className="qf-cal" data-macro-kind="calendar" style={{ position: "relative" }}>
      <style>{CSS}</style>
      {spark.card}
      <div className="qf-cal-head">
        <button type="button" className="qf-cal-pill" aria-label="Previous" onClick={prev}>◀</button>
        <b className="qf-cal-label" data-testid="cal-label">
          {cfg.range === "week" ? weekLabel(weekStart(anchor)) : monthLabel(`${month}-01`)}
        </b>
        <button type="button" className="qf-cal-pill" aria-label="Next" onClick={next}>▶</button>
        <span className="qf-cal-lbl">Range</span>
        <Seg value={cfg.range} onChange={setRange} label="Range" options={[["week", "Week"], ["month", "Month"]]} />
        <span className="qf-cal-lbl">Layout</span>
        <Seg value={cfg.layout} onChange={(layout) => set({ layout })} label="Layout" options={[["list", "List"], ["grid", "Grid"]]} />
        <span className="qf-cal-lbl">View</span>
        {/* In the grid, Merged and Side by side draw the same week (owner, 2026-11-02):
            only which kinds show matters there, so it offers three, and a saved
            "side" reads as Both. The list keeps all four. */}
        {cfg.layout === "grid" ? (
          <Seg
            value={cfg.view === "side" ? "merged" : cfg.view}
            onChange={(view) => set({ view })}
            label="View"
            options={[["merged", "Both"], ["econ", "Economic"], ["earn", "Earnings"]]}
          />
        ) : (
          <Seg
            value={cfg.view}
            onChange={(view) => set({ view })}
            label="View"
            options={[["merged", "Merged"], ["side", "Side by side"], ["econ", "Economic"], ["earn", "Earnings"]]}
          />
        )}
        <Seg value={keyOnly ? "key" : "all"} onChange={(v) => setKeyOnly(v === "key")} label="Releases" options={[["key", "★ Key"], ["all", "All"]]} />
        <Seg value={tz} onChange={setTz} label="Times" options={[["ET", "ET"], ["PT", "PT"]]} />
        <span style={{ flex: 1 }} />
        {chartJump && (
          <button
            type="button"
            className="qf-cal-pill"
            aria-label="Jump layout"
            title="Clicking a symbol opens it on a chart in this layout"
            onClick={() => setChooser({})}
          >
            ⤴ {jumpTo.kind === "go" ? jumpTo.target.name : "Choose layout"} ▾
          </button>
        )}
        {upcoming && (
          <span className="qf-cal-pill" data-testid="cal-next">
            Next: <b className="qf-cal-acc">{upcoming.name}</b> {shortDayLabel(upcoming.date).weekday}{" "}
            {formatTime(upcoming.time, tz)} {tz}
          </span>
        )}
      </div>
      {showEarn && (
        <div className="qf-cal-head qf-cal-head2">
          <span className="qf-cal-lbl">Earnings from</span>
          <div className="qf-cal-seg" role="group" aria-label="Earnings from">
            {CALENDAR_SOURCES.map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={cfg.earningsSources.includes(s)}
                onClick={() => set({ earningsSources: toggleSource(cfg.earningsSources, s) })}
              >
                {SOURCE_LABEL[s].icon} {SOURCE_LABEL[s].label}
              </button>
            ))}
          </div>
          <span className="qf-cal-lbl">Top cap</span>
          <Seg
            value={String(cfg.topCap)}
            onChange={(v) => set({ topCap: Number(v) as 50 | 100 | 500 | 1000 })}
            label="Top cap"
            options={[["50", "50"], ["100", "100"], ["500", "500"], ["1000", "1000"]]}
          />
          <span className="qf-cal-lbl">Per day</span>
          <Seg
            value={String(cfg.perDay)}
            onChange={(v) => {
              setMore(new Set());
              set({ perDay: Number(v) });
            }}
            label="Per day"
            options={[["5", "5"], ["8", "8"], ["0", "All matched"]]}
          />
          <span className="qf-cal-lbl">Group</span>
          <Seg
            value={cfg.group}
            onChange={(group) => {
              setMore(new Set());
              set({ group });
            }}
            label="Group"
            options={[["none", "None"], ["sector", "Sector"]]}
          />
          <span className="qf-cal-lbl">Find</span>
          <input
            className="qf-cal-find"
            aria-label="Find a symbol's earnings"
            placeholder="Symbol"
            value={findText}
            spellCheck={false}
            onChange={(e) => setFindText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runFind();
              if (e.key === "Escape") {
                setFindText("");
                setFound(null);
                setShown(null);
              }
            }}
          />
          {found && <FindResult found={found} onShow={showFound} onClear={() => { setFindText(""); setFound(null); setShown(null); }} />}
        </div>
      )}
      {shown && <style>{`.qf-cal [data-symbol="${cssSymbol(shown)}"],.qf-cal tr[data-id$=".${cssSymbol(shown)}"]{outline:2px solid var(--qf-accent);outline-offset:-1px}`}</style>}
      {chooser && (
        <JumpChooser
          targets={targets}
          current={jumpTo.kind === "go" ? jumpTo.target.id : undefined}
          onPick={choose}
          onClose={() => setChooser(null)}
        />
      )}
      <Status earn={showEarn ? earn : undefined} econ={cfg.view !== "earn" ? econ : undefined} />
      <div className="qf-cal-body">{body}</div>
    </div>
  );
}

// ── shared pieces ────────────────────────────────────────────────────────

interface Ctx {
  tz: CalendarTz;
  open: ReadonlySet<string>;
  more: ReadonlySet<string>;
  shut: ReadonlySet<string>;
  /** Collapsed sector headers in the list, `iso|block|sector`. */
  shutSec: ReadonlySet<string>;
  hiDay: string | null;
  /** `YYYY-MM` of the view; month views skip days outside it. */
  month: string;
  range: "week" | "month";
  view: "merged" | "side" | "econ" | "earn";
  perDay: number;
  econOf: (iso: string) => EconEvent[];
  earnOf: (iso: string, limit?: number) => EarnSlice;
  /** Chart this symbol in the jump layout; absent, symbols are not clickable. */
  pick?: (symbol: string) => void;
  toggleSec: (key: string) => void;
  toggleOpen: (id: string) => void;
  toggleMore: (iso: string) => void;
  toggleDay: (iso: string) => void;
  openWeek: (iso: string) => void;
  /** The screener's 60-bar hover card (SparkCard), for a symbol under the pointer; null closes it. */
  hover: (symbol: string | null, el: HTMLElement | null) => void;
}

function Seg<V extends string>({
  value, onChange, options, label,
}: {
  value: V;
  onChange: (v: V) => void;
  options: [V, string][];
  label: string;
}) {
  return (
    <div className="qf-cal-seg" role="group" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" aria-pressed={v === value} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

/** "Jump to which layout?" — the pick is kept and used from then on (owner, 2026-11-01). */
function JumpChooser({
  targets, current, onPick, onClose,
}: {
  targets: readonly JumpTarget[];
  current?: string;
  onPick: (t: JumpTarget) => void;
  onClose: () => void;
}) {
  return (
    <div
      className="qf-cal-jump"
      role="dialog"
      aria-label="Jump to which layout?"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div className="qf-cal-jumph">
        <b>Jump to which layout?</b>
        <button type="button" aria-label="Close" onClick={onClose}>×</button>
      </div>
      {targets.length ? (
        targets.map((t) => (
          <button
            key={t.id}
            type="button"
            className="qf-cal-jumpi"
            aria-pressed={t.id === current}
            onClick={() => onPick(t)}
          >
            {t.name}
          </button>
        ))
      ) : (
        <div className="qf-cal-dim">No layout has a chart yet.</div>
      )}
      <div className="qf-cal-dim qf-cal-jumpf">Remembered for this calendar · change it with ⤴ above</div>
    </div>
  );
}

/** A symbol that charts on click; stops the row or cell under it toggling too. */
function clickSymbol(ctx: Ctx, symbol: string) {
  const pick = ctx.pick;
  return pick
    ? (e: MouseEvent) => {
        e.stopPropagation();
        pick(symbol);
      }
    : undefined;
}

/** A symbol made safe for an attribute selector (letters, digits, `.`, `-` only). */
function cssSymbol(s: string): string {
  return s.replace(/[^A-Z0-9.-]/gi, "");
}

const TIME_TEXT = { bmo: "before the open", amc: "after the close" } as const;

/** Find's one line: when the symbol reports, and whether the filters show it. */
function FindResult({
  found, onShow, onClear,
}: { found: Load<EarningsFind>; onShow: (f: EarningsFind) => void; onClear: () => void }) {
  let body: ReactNode;
  if (found.state === "loading") body = <span>Finding…</span>;
  else if (found.state === "error") body = <span className="qf-cal-neg">{found.message}</span>;
  else if (found.state !== "ok") body = null;
  else {
    const f = found.data;
    const rank = f.capRank != null ? ` (rank ${f.capRank})` : "";
    if (!f.report) {
      body = (
        <span data-testid="cal-find">
          <b>{f.symbol}</b> — no report found through {shortDayLabel(f.lookedTo).date}
        </span>
      );
    } else {
      const day = shortDayLabel(f.report.date);
      const when = f.report.time ? ` · ${TIME_TEXT[f.report.time]}${f.report.timeSource === "slot" ? " (usual time)" : ""}` : "";
      const est = f.report.epsEstimated != null ? ` · est ${formatEps(f.report.epsEstimated)}` : "";
      const filters = f.sources.length
        ? `in your filters: ${f.sources.map((s) => SOURCE_LABEL[fromWireSource(s)!].label).join(", ")}`
        : `outside your filters${rank}`;
      body = (
        <span data-testid="cal-find">
          <b>{f.symbol}</b> · {day.weekday} {day.date}
          {when}
          {est} · <span className={f.sources.length ? undefined : "qf-cal-dim"}>{filters}</span>{" "}
          <button type="button" className="qf-cal-pill" onClick={() => onShow(f)}>Show</button>
        </span>
      );
    }
  }
  return (
    <span className="qf-cal-findres">
      {body}
      <button type="button" className="qf-cal-pill" aria-label="Clear find" onClick={onClear}>✕</button>
    </span>
  );
}

function Status({ earn, econ }: { earn?: Load<EarningsPayload>; econ?: Load<EconPayload> }) {
  const notes: ReactNode[] = [];
  if (econ?.state === "off") notes.push(<span key="co">Economic: no data server configured.</span>);
  if (econ?.state === "error") notes.push(<span key="ee" className="qf-cal-neg">Economic: {econ.message}</span>);
  if (earn?.state === "loading") notes.push(<span key="el">Loading earnings…</span>);
  if (earn?.state === "off") notes.push(<span key="eo">Earnings: no data server configured.</span>);
  if (earn?.state === "error") notes.push(<span key="er" className="qf-cal-neg">Earnings: {earn.message}</span>);
  if (earn?.state === "ok" && !earn.data.available)
    notes.push(<span key="ea">Earnings: the calendar has not been built on the server yet.</span>);
  if (!notes.length) return null;
  return (
    <div className="qf-cal-status" role="status">
      {notes}
    </div>
  );
}

/** The screener's logo template, with a letter tile when FMP has no image. */
function Logo({ symbol, size }: { symbol: string; size: number }) {
  const [failed, setFailed] = useState(false);
  const url = resolveImageUrl(LOGO_COLUMN.imageUrl!, { symbol } as Parameters<typeof resolveImageUrl>[1]);
  useEffect(() => setFailed(false), [url]);
  const box = { width: size, height: size };
  if (!url || failed)
    return (
      <span className="qf-cal-lgf" style={box} aria-hidden>
        {symbol[0]}
      </span>
    );
  return <img className="qf-cal-lg" style={box} src={url} alt="" loading="lazy" onError={() => setFailed(true)} />;
}

function Chips({ row }: { row: EarningsRow }) {
  return (
    <>
      {rowSources(row).map((s) => (
        <span
          key={s}
          className="qf-cal-chip"
          title={SOURCE_LABEL[s].label + (s === "topCap" && row.capRank != null ? ` (rank ${row.capRank})` : "")}
        >
          {SOURCE_LABEL[s].icon}
        </span>
      ))}
      {row.sources.includes("find") && (
        <span className="qf-cal-chip" title="Found — outside your filters">
          🔎
        </span>
      )}
    </>
  );
}

function When({ row }: { row: EarningsRow }) {
  const slot = row.timeSource === "slot";
  const text = row.time === "bmo" ? "BMO" : row.time === "amc" ? "AMC" : "ER";
  const title =
    row.time == null
      ? "Report time not announced"
      : slot
        ? "This company's usual time — not confirmed for this report"
        : row.time === "bmo"
          ? "Before the open"
          : "After the close";
  return (
    <span className={`qf-cal-when ${row.time ?? "none"}${slot ? " slot" : ""}`} title={title}>
      {text}
    </span>
  );
}

function Bars({ values, highlightLast, signed }: { values: number[]; highlightLast?: boolean; signed?: boolean }) {
  const W = 200;
  const H = 50;
  const bw = W / Math.max(1, values.length);
  if (signed) {
    const mx = Math.max(...values.map(Math.abs)) || 1;
    const z = H / 2;
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="qf-cal-bars" role="img" aria-label="EPS surprise history">
        <line x1={0} x2={W} y1={z} y2={z} style={{ stroke: "var(--qf-border)" }} />
        {values.map((v, i) => {
          const h = (Math.abs(v) / mx) * 22;
          return (
            <rect
              key={i}
              x={i * bw + 3}
              y={v >= 0 ? z - h : z}
              width={Math.max(1, bw - 6)}
              height={Math.max(1, h)}
              style={{ fill: v >= 0 ? "var(--qf-positive)" : "var(--qf-negative)" }}
            />
          );
        })}
      </svg>
    );
  }
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="qf-cal-bars" role="img" aria-label="Release history">
      {values.map((v, i) => {
        const h = ((v - lo) / span) * 40 + 2;
        const last = highlightLast && i === values.length - 1;
        return (
          <rect
            key={i}
            x={i * bw + 2}
            y={H - 2 - h}
            width={Math.max(1, bw - 4)}
            height={h}
            style={{ fill: last ? "var(--qf-accent)" : "var(--qf-accent-blue)", opacity: last ? 1 : 0.55 }}
          />
        );
      })}
    </svg>
  );
}

// ── list ─────────────────────────────────────────────────────────────────

const HEAD = {
  econ: ["Time (TZ)", "Report", "Period", "Actual", "Forecast", "Previous"],
  earn: ["When", "Symbol", "Cap · history", "EPS", "Est.", "Last Q"],
  merged: ["Time (TZ)", "Event", "Period · cap", "Actual", "Forecast / est.", "Previous"],
} as const;

function ListTable({ ctx, kind, days }: { ctx: Ctx; kind: "econ" | "earn" | "merged"; days: string[] }) {
  const rows: ReactNode[] = [];
  let week: string | null = null;
  for (const iso of days) {
    if (ctx.range === "month") {
      if (monthOf(iso) !== ctx.month) continue;
      const w = weekStart(iso);
      if (w !== week) {
        week = w;
        rows.push(
          <tr key={`w${w}`} className="qf-cal-wk">
            <td colSpan={7}>Week of {shortDayLabel(w).date}</td>
          </tr>,
        );
      }
    }
    const ec = kind === "earn" ? [] : ctx.econOf(iso);
    const e: EarnSlice = kind === "econ" ? { show: [], hidden: 0, matchedHidden: 0 } : ctx.earnOf(iso);
    const n = ec.length + e.show.length;
    const sh = ctx.shut.has(iso);
    rows.push(
      <tr
        key={`d${iso}`}
        className={`qf-cal-day${iso === ctx.hiDay ? " hi" : ""}`}
        data-day={iso}
        onClick={() => ctx.toggleDay(iso)}
      >
        <td className="qf-cal-chev">{sh ? "▸" : "▾"}</td>
        <td colSpan={6}>
          {dayLabel(iso)}
          {n > 0 && <span className="qf-cal-dim"> · {n}</span>}
        </td>
      </tr>,
    );
    if (sh) continue;
    const merged = kind === "merged";
    if (!n) {
      rows.push(
        <tr key={`e${iso}`} className="qf-cal-empty">
          <td colSpan={7}>{kind === "earn" ? "None from your sources" : "No events scheduled"}</td>
        </tr>,
      );
    }
    const sub = (label: string) => (
      <tr key={`s${iso}${label}`} className="qf-cal-sub">
        <td />
        <td colSpan={6}>{label}</td>
      </tr>
    );
    const inBlock = (b: TimeBlock) => e.show.filter((r) => blockOf(r) === b);
    // One time block's rows: under sector headers with Group: Sector.
    const block = (b: TimeBlock): ReactNode[] => {
      if (!inBlock(b).length) return [];
      const body = isGrouped(e)
        ? e.blocks[b].flatMap((g) => sectorRows(ctx, iso, b, g, merged))
        : inBlock(b).flatMap((r) => earnRows(ctx, iso, r, merged));
      return [sub(BLOCK_LABEL[b]), ...body];
    };
    if (kind === "econ") {
      ec.forEach((ev) => rows.push(...econRows(ctx, ev, false)));
    } else if (kind === "earn") {
      if (isGrouped(e)) rows.push(...block("bmo"), ...block("amc"), ...block("none"));
      else e.show.forEach((r) => rows.push(...earnRows(ctx, iso, r, false)));
    } else {
      rows.push(...block("bmo"));
      if (ec.length && e.show.length) rows.push(sub("Releases"));
      ec.forEach((ev) => rows.push(...econRows(ctx, ev, merged)));
      rows.push(...block("amc"), ...block("none"));
    }
    if (kind !== "econ") {
      const m = moreRow(ctx, iso, e);
      if (m) rows.push(m);
    }
  }
  return (
    <div style={{ overflowX: "auto" }}>
      <table className="qf-cal-tbl" data-testid={`cal-table-${kind}`}>
        <thead>
          <tr>
            <th />
            {HEAD[kind].map((h, i) => (
              <th key={h} className={i >= 3 ? "r" : undefined}>
                {h.replace("TZ", ctx.tz)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

function econRows(ctx: Ctx, ev: EconEvent, merged: boolean): ReactNode[] {
  const id = `c.${ev.id}`;
  const o = ctx.open.has(id);
  const cls = surpriseClass(ev.actual, ev.forecast);
  const out: ReactNode[] = [
    <tr key={id} className={`qf-cal-ev${ev.key ? " key" : ""}`} data-id={id} onClick={() => ctx.toggleOpen(id)}>
      <td className="qf-cal-chev">{o ? "▴" : "▾"}</td>
      <td className="qf-cal-dim qf-cal-nw">{formatTime(ev.time, ctx.tz)}</td>
      <td>
        {merged && <span className="qf-cal-kind eco">ECO</span>}
        {ev.name}
        {ev.key && <span className="qf-cal-star">★</span>}
      </td>
      <td className="qf-cal-dim">{ev.period ?? "-"}</td>
      <td className={`r ${cls}`}>{ev.actual == null ? <span className="qf-cal-dim">-</span> : <b>{ev.actual}</b>}</td>
      <td className="r qf-cal-fc">{ev.forecast ?? "-"}</td>
      <td className="r">{ev.previous ?? "-"}</td>
    </tr>,
  ];
  if (o)
    out.push(
      <tr key={`${id}.d`} className="qf-cal-det">
        <td colSpan={7}>
          <div className="qf-cal-detg">
            {ev.history?.length ? (
              <>
                <Bars values={ev.history} highlightLast />
                <div>
                  Last {ev.history.length} releases · <b>{ev.name}</b>
                </div>
              </>
            ) : (
              <span className="qf-cal-dim">No past releases of this report in view.</span>
            )}
          </div>
        </td>
      </tr>,
    );
  return out;
}

function earnRows(ctx: Ctx, iso: string, r: EarningsRow, merged: boolean): ReactNode[] {
  const id = `e.${iso}.${r.symbol}`;
  const o = ctx.open.has(id);
  const cls = epsClass(r.epsActual, r.epsEstimated);
  const history = r.beats != null && r.beatsOf != null ? `${r.beats}/${r.beatsOf} beat` : "";
  const cap = formatCap(r.marketCap);
  const out: ReactNode[] = [
    <tr key={id} className="qf-cal-ev earn" data-id={id} onClick={() => ctx.toggleOpen(id)}>
      <td className="qf-cal-chev">{o ? "▴" : "▾"}</td>
      <td className="qf-cal-nw">
        <When row={r} />
      </td>
      <td>
        <span className="qf-cal-erow">
          {merged && <span className="qf-cal-kind er">ER</span>}
          <Logo symbol={r.symbol} size={20} />
          <b
            className={`qf-cal-sym${ctx.pick ? " qf-cal-link" : ""}`}
            onMouseEnter={(e) => ctx.hover(r.symbol, e.currentTarget)}
            onMouseLeave={() => ctx.hover(null, null)}
            onClick={clickSymbol(ctx, r.symbol)}
            title={ctx.pick ? `Chart ${r.symbol}` : undefined}
          >
            {r.symbol}
          </b>{" "}
          {r.name && <span className="qf-cal-dim qf-cal-nm">{r.name}</span>}
          <Chips row={r} />
        </span>
      </td>
      <td className="qf-cal-dim qf-cal-nw">{[cap, history].filter(Boolean).join(" · ")}</td>
      <td className={`r ${cls}`}>{r.epsActual == null ? <span className="qf-cal-dim">-</span> : <b>{formatEps(r.epsActual)}</b>}</td>
      <td className="r qf-cal-fc">{formatEps(r.epsEstimated)}<VsLast r={r} /></td>
      <td className="r">{formatEps(r.lastQuarterEps)}</td>
    </tr>,
  ];
  if (o)
    out.push(
      <tr key={`${id}.d`} className="qf-cal-det">
        <td colSpan={7}>
          <div className="qf-cal-detg">
            <Logo symbol={r.symbol} size={40} />
            {r.surprises.length > 0 && <Bars values={r.surprises} signed />}
            <div>
              {r.surprises.length > 0 ? `EPS surprise, last ${r.surprises.length} quarters` : "No reported quarters yet"} ·{" "}
              <b>{r.symbol}</b> {r.name}
              <br />
              <span className="qf-cal-dim">
                Why shown:{" "}
                {rowSources(r)
                  .map((s) => `${SOURCE_LABEL[s].icon} ${SOURCE_LABEL[s].label}${s === "topCap" && r.capRank != null ? ` #${r.capRank}` : ""}`)
                  .join(" · ")}
              </span>
              {r.fiscalPeriod && (
                <>
                  <br />
                  <span className="qf-cal-dim">Reporting {r.fiscalPeriod}{r.periodEnding ? `, period ending ${r.periodEnding}` : ""}</span>
                </>
              )}
            </div>
          </div>
        </td>
      </tr>,
    );
  return out;
}

/** A sector header ("Technology ·3", collapsible) and its rows. */
function sectorRows(ctx: Ctx, iso: string, b: TimeBlock, g: SectorGroup, merged: boolean): ReactNode[] {
  const key = `${iso}|${b}|${g.label}`;
  const sh = ctx.shutSec.has(key);
  return [
    <tr key={`g${key}`} className="qf-cal-sec" data-sector={g.label} onClick={() => ctx.toggleSec(key)}>
      <td className="qf-cal-chev">{sh ? "▸" : "▾"}</td>
      <td colSpan={6}>
        {g.label}
        <span className="qf-cal-dim"> ·{g.total}</span>
      </td>
    </tr>,
    ...(sh ? [] : g.rows.flatMap((r) => earnRows(ctx, iso, r, merged))),
  ];
}

function moreRow(ctx: Ctx, iso: string, e: EarnSlice): ReactNode {
  const expanded = ctx.more.has(iso);
  if (!e.hidden && !expanded) return null;
  if (isGrouped(e))
    return (
      <tr key={`m${iso}`} className="qf-cal-more" data-more={iso} onClick={() => ctx.toggleMore(iso)}>
        <td />
        <td colSpan={6}>
          {expanded ? (
            `▴ Back to top ${SECTOR_CAP} per sector`
          ) : (
            <>
              +{e.hidden} more reporting
              {e.hiddenBySector.length > 0 && <span className="qf-cal-acc"> · {hiddenBreakdown(e.hiddenBySector)}</span>}
              {e.matchedHidden > 0 && " · show all"}
            </>
          )}
        </td>
      </tr>
    );
  return (
    <tr key={`m${iso}`} className="qf-cal-more" data-more={iso} onClick={() => ctx.toggleMore(iso)}>
      <td />
      <td colSpan={6}>
        {expanded ? (
          `▴ Back to top ${ctx.perDay || "matched"}`
        ) : (
          <>
            +{e.hidden} more reporting
            {e.matchedHidden > 0 && <span className="qf-cal-acc"> ({e.matchedHidden} match your sources)</span>}
            {e.matchedHidden > 0 && " · show matched"}
          </>
        )}
      </td>
    </tr>
  );
}

/** The estimate against last quarter, coloured by direction; nothing when either is unknown. */
function VsLast({ r }: { r: EarningsRow }) {
  const v = estVsLast(r.epsEstimated, r.lastQuarterEps);
  if (!v) return null;
  return (
    <span className={`qf-cal-vs ${v.cls}`} title={`Estimate vs last quarter's ${formatEps(r.lastQuarterEps)}`}>
      {" "}
      {v.text}
    </span>
  );
}

// ── grid ─────────────────────────────────────────────────────────────────

function Tile({ r, hover, pick }: { r: EarningsRow; hover?: Ctx["hover"]; pick?: Ctx["pick"] }) {
  const cls = epsClass(r.epsActual, r.epsEstimated);
  return (
    <div
      className={`qf-cal-etile${pick ? " qf-cal-link" : ""}`}
      data-symbol={r.symbol}
      role={pick ? "button" : undefined}
      onClick={pick ? () => pick(r.symbol) : undefined}
      onMouseEnter={hover ? (e) => hover(r.symbol, e.currentTarget) : undefined}
      onMouseLeave={hover ? () => hover(null, null) : undefined}
      title={`${r.name ?? r.symbol} · EPS est ${formatEps(r.epsEstimated)} · last ${formatEps(r.lastQuarterEps)}`}
    >
      <Logo symbol={r.symbol} size={30} />
      <span className="qf-cal-es">
        <b className="qf-cal-sym">{r.symbol}</b>
        <Chips row={r} />
        <span className="qf-cal-dim" style={{ fontSize: 13 }}>
          {r.epsActual == null ? (
            <>
              est {formatEps(r.epsEstimated)}
              <VsLast r={r} />
            </>
          ) : (
            <>
              <b className={cls}>{formatEps(r.epsActual)}</b> vs {formatEps(r.epsEstimated)}
            </>
          )}
        </span>
      </span>
    </div>
  );
}

function GridWeek({ ctx, days }: { ctx: Ctx; days: string[] }) {
  // A busy day lays its tiles in two columns and takes a double share of the
  // width; a quiet day stays one column wide (owner, 2026-11-01).
  const cols = days.map((iso) => dayColumns(ctx.view === "econ" ? 0 : ctx.earnOf(iso).show.length));
  return (
    <div
      className="qf-cal-gw"
      data-testid="cal-grid-week"
      // A variable, not `gridTemplateColumns`: inline style would beat the
      // narrow-screen rule that stacks the days.
      style={{ ["--qf-cal-days" as string]: cols.map((c) => `minmax(0,${c}fr)`).join(" ") }}
    >
      {days.map((iso, di) => {
        const tiles = (rows: EarningsRow[]) => (
          <div className="qf-cal-tiles" style={{ gridTemplateColumns: `repeat(${cols[di]},minmax(0,1fr))` }}>
            {rows.map((r) => <Tile key={r.symbol} r={r} hover={ctx.hover} pick={ctx.pick} />)}
          </div>
        );
        const ec = ctx.econOf(iso);
        const e = ctx.earnOf(iso);
        const earnOn = ctx.view !== "econ";
        // A block's tiles; with Group: Sector each sector's under its header (not collapsible).
        const block = (b: TimeBlock) => {
          const rows = e.show.filter((r) => blockOf(r) === b);
          if (!earnOn || !rows.length) return null;
          return (
            <>
              {/* Grouped, the block's label rides on its first sector's row (owner,
                  2026-11-01) instead of taking a row of its own. */}
              {!isGrouped(e) && <div className="qf-cal-gs">{BLOCK_LABEL[b]}</div>}
              {isGrouped(e)
                ? e.blocks[b].map((g, gi) => (
                    <div key={g.label}>
                      <div className="qf-cal-gsec" data-sector={g.label}>
                        <span>
                          {g.label}
                          <span className="qf-cal-dim"> ·{g.total}</span>
                        </span>
                        {gi === 0 && <span className="qf-cal-gs qf-cal-gs-in">{BLOCK_LABEL[b]}</span>}
                      </div>
                      {tiles(g.rows)}
                    </div>
                  ))
                : tiles(rows)}
            </>
          );
        };
        const { weekday, date } = shortDayLabel(iso);
        return (
          <div key={iso} className={`qf-cal-gc${iso === ctx.hiDay ? " hi" : ""}`}>
            <div className="qf-cal-gh">
              <b>{weekday}</b> {date}
            </div>
            {/* Releases first, above the earnings tiles (owner, 2026-11-02): as one grey
                line between the two tile blocks they lost to the logos. A ★ key
                release gets an accent bar and a second line with its numbers. */}
            {ctx.view !== "earn" && (
              <div className="qf-cal-rls" data-testid="cal-grid-releases">
                <div className="qf-cal-gs">Releases</div>
                {ec.length ? (
                  ec.map((ev) => (
                    <div
                      key={ev.id}
                      className={`qf-cal-rl${ev.key ? " key" : ""}`}
                      title={`${ev.name} · forecast ${ev.forecast ?? "-"} · previous ${ev.previous ?? "-"}`}
                    >
                      <div>
                        <span className="qf-cal-dim">{formatTime(ev.time, ctx.tz)}</span> {ev.name}
                        {ev.key && <span className="qf-cal-star">★</span>}
                        {!ev.key && ev.actual != null && (
                          <>
                            {" "}
                            <b className={surpriseClass(ev.actual, ev.forecast)}>{ev.actual}</b>
                          </>
                        )}
                      </div>
                      {ev.key && (
                        <div className="qf-cal-rl2">
                          <b className={ev.actual != null ? surpriseClass(ev.actual, ev.forecast) : "qf-cal-dim"}>
                            {ev.actual ?? "—"}
                          </b>
                          <span className="qf-cal-dim">
                            fc {ev.forecast ?? "-"} · prev {ev.previous ?? "-"}
                          </span>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="qf-cal-rl qf-cal-dim" style={{ fontStyle: "italic" }}>
                    No events scheduled
                  </div>
                )}
              </div>
            )}
            {block("bmo")}
            {block("amc")}
            {block("none")}
            {earnOn && (e.hidden > 0 || ctx.more.has(iso)) && (
              <div className="qf-cal-gmore" data-more={iso} role="button" tabIndex={0} onClick={() => ctx.toggleMore(iso)}>
                {ctx.more.has(iso) ? (
                  isGrouped(e) ? `▴ top ${SECTOR_CAP} per sector` : `▴ top ${ctx.perDay || "matched"}`
                ) : (
                  <>
                    +{e.hidden} more
                    {isGrouped(e) && e.hiddenBySector.length > 0 && (
                      <div className="qf-cal-acc qf-cal-gbrk">{hiddenBreakdown(e.hiddenBySector)}</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GridMonth({ ctx }: { ctx: Ctx }) {
  const weeks = monthWeeks(`${ctx.month}-01`);
  return (
    <div className="qf-cal-gm" data-testid="cal-grid-month">
      {["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => (
        <div key={d} className="qf-cal-gmh">
          {d}
        </div>
      ))}
      {weeks.flatMap(weekDays).map((iso) => {
        const inMonth = monthOf(iso) === ctx.month;
        const ec = ctx.econOf(iso).filter((ev) => ev.key);
        const e = ctx.earnOf(iso, MONTH_CELL_LOGOS);
        return (
          <div
            key={iso}
            className={`qf-cal-gmc${inMonth ? "" : " out"}${iso === ctx.hiDay ? " hi" : ""}`}
            data-iso={iso}
            role={inMonth ? "button" : undefined}
            tabIndex={inMonth ? 0 : undefined}
            aria-label={inMonth ? `Open the week of ${dayLabel(iso)}` : undefined}
            onClick={inMonth ? () => ctx.openWeek(iso) : undefined}
          >
            <div className="qf-cal-gmd">{dayOfMonth(iso)}</div>
            {inMonth && ctx.view !== "earn" && (
              <>
                {ec.slice(0, MONTH_CELL_RELEASES).map((ev) => (
                  <div key={ev.id} className="qf-cal-mrl" title={`${formatTime(ev.time, ctx.tz)} ${ev.name}`}>
                    {ev.name}
                  </div>
                ))}
                {ec.length > MONTH_CELL_RELEASES && (
                  <div className="qf-cal-mrl qf-cal-dim">+{ec.length - MONTH_CELL_RELEASES} more releases</div>
                )}
              </>
            )}
            {inMonth && ctx.view !== "econ" && e.show.length > 0 && (
              <div className="qf-cal-mlogos">
                {e.show.map((r) => (
                  <span
                    key={r.symbol}
                    title={`${r.symbol} · ${r.time ? r.time.toUpperCase() : "time not announced"}`}
                    data-symbol={r.symbol}
                    className={ctx.pick ? "qf-cal-link" : undefined}
                    onMouseEnter={(ev) => ctx.hover(r.symbol, ev.currentTarget)}
                    onMouseLeave={() => ctx.hover(null, null)}
                    onClick={clickSymbol(ctx, r.symbol)}
                  >
                    <Logo symbol={r.symbol} size={20} />
                  </span>
                ))}
                {e.hidden > 0 && <span className="qf-cal-dim" style={{ fontSize: 10 }}>+{e.hidden}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── styles: tokens only (docs/features/theming.md) ──────────────────────

/** The app's own font (Inter), not a monospace (owner, 2026-11-02); numbers line up via tabular-nums on the host. */
const MONO = "inherit";
const tint = (token: string, pct: number) => `color-mix(in srgb, var(${token}) ${pct}%, transparent)`;

const CSS = `
.qf-cal{width:100%;height:100%;overflow:auto;display:flex;flex-direction:column;background:var(--qf-surface);color:var(--qf-text);font-size:14px;font-variant-numeric:tabular-nums}
.qf-cal button{font:inherit;color:inherit;background:none;border:0;cursor:pointer}
.qf-cal-head{display:flex;align-items:center;gap:8px;padding:7px 12px;background:var(--qf-surface-alt);border-bottom:1px solid var(--qf-border);flex-wrap:wrap}
.qf-cal-head2{background:var(--qf-background)}
.qf-cal-label{font-family:${MONO};font-size:14.5px;min-width:120px;text-align:center}
.qf-cal-find{width:84px;font-size:12.5px;padding:2px 6px;border:1px solid var(--qf-border);border-radius:4px;background:var(--qf-surface);color:var(--qf-text);text-transform:uppercase}
.qf-cal-findres{display:inline-flex;gap:6px;align-items:center;font-size:12.5px;color:var(--qf-text)}
.qf-cal-lbl{font-family:${MONO};font-size:12.5px;color:var(--qf-text-muted)}
.qf-cal-pill{font-family:${MONO};font-size:11px;padding:1px 7px;border-radius:99px;border:1px solid var(--qf-border)!important;color:var(--qf-text-secondary)!important}
.qf-cal-seg{display:flex;border:1px solid var(--qf-border);border-radius:5px;overflow:hidden}
.qf-cal-seg button{padding:3px 9px;font-family:${MONO};font-size:12.5px;color:var(--qf-text-secondary)}
.qf-cal-seg button:hover{background:var(--qf-row-hover)}
.qf-cal-seg button[aria-pressed="true"]{background:var(--qf-selection);color:var(--qf-accent)}
.qf-cal-status{display:flex;flex-direction:column;gap:2px;padding:5px 12px;font-size:11.5px;color:var(--qf-text-secondary);border-bottom:1px solid var(--qf-border)}
.qf-cal-body{padding:6px;flex:1;min-height:0;display:flex;flex-direction:column}
.qf-cal-acc{color:var(--qf-accent)} .qf-cal-neg{color:var(--qf-negative)}
.qf-cal-dim{color:var(--qf-text-muted)} .qf-cal-nw{white-space:nowrap}
.qf-cal .pos{color:var(--qf-positive)} .qf-cal .neg{color:var(--qf-negative)}
.qf-cal-side{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:14px}
@media (max-width:1000px){.qf-cal-side{grid-template-columns:1fr}}
.qf-cal-colt{font-family:${MONO};font-size:11.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--qf-accent);padding:2px 6px 6px}
.qf-cal-tbl{width:100%;border-collapse:collapse;font-size:13.5px}
.qf-cal-tbl th{font-family:${MONO};font-size:10.5px;letter-spacing:.06em;text-transform:uppercase;color:var(--qf-text-muted);font-weight:500;text-align:left;padding:6px 7px;border-bottom:1px solid var(--qf-border);white-space:nowrap}
.qf-cal-tbl td{padding:5px 6px;border-bottom:1px solid ${tint("--qf-border", 60)};vertical-align:middle}
.qf-cal-tbl tbody tr:nth-child(even) td{background:var(--qf-row-alt)}
.qf-cal-tbl .r{text-align:right;font-family:${MONO};font-variant-numeric:tabular-nums;white-space:nowrap}
.qf-cal-tbl th.r{text-align:right}
.qf-cal-day{cursor:pointer}
.qf-cal-tbl tr.qf-cal-day td{background:var(--qf-background);font-family:${MONO};font-size:12px;color:var(--qf-accent);letter-spacing:.06em;text-transform:uppercase;padding-top:9px;border-bottom:1px solid var(--qf-border)}
.qf-cal-tbl tr.qf-cal-day.hi td{box-shadow:inset 3px 0 var(--qf-accent)}
.qf-cal-tbl tr.qf-cal-wk td{background:transparent;font-family:${MONO};font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--qf-warn);padding-top:14px;border-bottom:1px solid ${tint("--qf-warn", 35)}}
.qf-cal-ev{cursor:pointer}
.qf-cal-tbl tr.qf-cal-ev:hover td{background:var(--qf-row-hover)}
.qf-cal-tbl tr.qf-cal-ev.key td:nth-child(3){color:var(--qf-text);font-weight:500}
.qf-cal-chev{width:16px;color:var(--qf-text-muted);font-size:10px}
.qf-cal-star{color:var(--qf-warn);font-size:11px;margin-left:4px}
.qf-cal-fc{color:var(--qf-positive)}
.qf-cal-tbl tr.qf-cal-empty td{color:var(--qf-text-muted);font-style:italic;padding-left:30px}
.qf-cal-tbl tr.qf-cal-sub td{background:transparent;font-family:${MONO};font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--qf-text-muted);padding-top:6px;border-bottom:0}
.qf-cal-more{cursor:pointer}
.qf-cal-tbl tr.qf-cal-more td{color:var(--qf-text-secondary);font-size:12.5px;font-style:italic}
.qf-cal-tbl tr.qf-cal-more:hover td{color:var(--qf-accent)}
.qf-cal-tbl tr.qf-cal-det td{background:var(--qf-background);padding:8px 10px 10px 30px}
.qf-cal-detg{display:flex;gap:18px;align-items:center;flex-wrap:wrap;font-size:12px;color:var(--qf-text-secondary)}
.qf-cal-bars{width:260px;height:56px}
.qf-cal-when{font-family:${MONO};font-size:10.5px;padding:1px 5px;border-radius:3px;border:1px solid var(--qf-border)}
.qf-cal-when.bmo{color:var(--qf-warn)}
.qf-cal-when.amc{color:var(--qf-accent-purple);border-color:${tint("--qf-accent-purple", 40)}}
.qf-cal-when.none{color:var(--qf-text-muted)}
.qf-cal-when.slot{border-style:dashed}
.qf-cal-kind{font-family:${MONO};font-size:9px;padding:0 4px;border-radius:2px;margin-right:4px}
.qf-cal-kind.eco{color:var(--qf-accent);border:1px solid ${tint("--qf-accent", 40)}}
.qf-cal-kind.er{color:var(--qf-positive);border:1px solid ${tint("--qf-positive", 45)}}
.qf-cal-sym{font-family:${MONO}} .qf-cal-nm{font-size:12.5px}
.qf-cal-erow{display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap}
.qf-cal-chip{display:inline-block;font-size:12px;margin-left:3px;padding:0 3px;border-radius:3px;background:var(--qf-row-hover)}
.qf-cal-lg{border-radius:3px;object-fit:contain;flex:0 0 auto;vertical-align:middle}
.qf-cal-lgf{display:inline-flex;align-items:center;justify-content:center;border-radius:4px;background:var(--qf-selection);color:var(--qf-text-secondary);font-family:${MONO};font-size:10px;font-weight:600;flex:0 0 auto;vertical-align:middle}
.qf-cal-gw{display:grid;grid-template-columns:var(--qf-cal-days,repeat(5,minmax(0,1fr)));gap:5px;flex:1;min-height:0;align-items:stretch}
.qf-cal-tiles{display:grid;gap:4px}
@media (max-width:900px){.qf-cal-gw{grid-template-columns:1fr}}
.qf-cal-gc{background:var(--qf-background);border:1px solid var(--qf-border);border-radius:5px;padding:4px 5px;display:flex;flex-direction:column;gap:3px;min-width:0;min-height:0;overflow-y:auto}
.qf-cal-gc.hi{border-color:var(--qf-accent)}
.qf-cal-gh{font-family:${MONO};font-size:14px;color:var(--qf-accent);padding:1px 2px 3px;border-bottom:1px solid var(--qf-border)}
.qf-cal-gs{font-family:${MONO};font-size:10.5px;letter-spacing:.08em;text-transform:uppercase;color:var(--qf-text-muted);margin-top:4px}
.qf-cal-etile{display:flex;align-items:center;gap:9px;padding:6px 8px;border-radius:4px;background:var(--qf-surface);border:1px solid ${tint("--qf-border", 60)}}
.qf-cal-etile:hover{border-color:var(--qf-border)}
.qf-cal-es{display:flex;flex-wrap:wrap;align-items:center;gap:2px 6px;min-width:0;font-size:15px}
.qf-cal-rls{display:flex;flex-direction:column;gap:3px}
.qf-cal-rl{font-size:14px;line-height:1.4;padding:2px 6px;color:var(--qf-text)}
.qf-cal-rl.key{border-left:3px solid var(--qf-accent);background:${tint("--qf-accent", 10)};border-radius:2px;padding:3px 6px}
.qf-cal-rl2{display:flex;gap:8px;align-items:baseline;font-size:12.5px;padding-left:2px}
.qf-cal-rl2 b{font-size:14px}
.qf-cal-gmore{font-size:12.5px;color:var(--qf-text-secondary);font-style:italic;cursor:pointer;padding:2px}
.qf-cal-gmore:hover{color:var(--qf-accent)}
.qf-cal-gm{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));grid-template-rows:auto;grid-auto-rows:minmax(108px,1fr);gap:4px;flex:1;min-height:0}
.qf-cal-gmh{font-family:${MONO};font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:var(--qf-text-muted);padding:2px 4px}
.qf-cal-gmc{background:var(--qf-background);border:1px solid var(--qf-border);border-radius:4px;padding:4px 5px;min-height:0;overflow:hidden;cursor:pointer;display:flex;flex-direction:column;gap:2px;min-width:0}
.qf-cal-gmc:hover{border-color:var(--qf-accent)}
.qf-cal-gmc.out{opacity:.35;cursor:default}
.qf-cal-gmc.out:hover{border-color:var(--qf-border)}
.qf-cal-gmc.hi{border-color:var(--qf-accent)}
.qf-cal-gmd{font-family:${MONO};font-size:12.5px;color:var(--qf-text-secondary)}
.qf-cal-vs{font-family:${MONO};font-size:1em;margin-left:2px}
.qf-cal-mrl{font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--qf-text)}
.qf-cal-link{cursor:pointer}
b.qf-cal-link:hover{color:var(--qf-accent);text-decoration:underline}
.qf-cal-sec{cursor:pointer}
.qf-cal-tbl tr.qf-cal-sec td{background:transparent;font-size:12px;color:var(--qf-text-secondary);font-weight:500;padding-top:4px;border-bottom:1px dashed ${tint("--qf-border", 70)}}
.qf-cal-gsec{font-size:12.5px;color:var(--qf-text-secondary);font-weight:500;margin:2px 0 1px;display:flex;justify-content:space-between;align-items:baseline;gap:6px}
.qf-cal-gs-in{margin-top:0}
.qf-cal-gbrk{font-size:11px;font-style:normal;margin-top:1px}
.qf-cal-jump{position:absolute;top:40px;right:12px;z-index:5;min-width:220px;max-width:320px;display:flex;flex-direction:column;gap:2px;padding:8px;background:var(--qf-surface);border:1px solid var(--qf-border);border-radius:6px;box-shadow:0 6px 18px ${tint("--qf-text", 18)};font-size:13px}
.qf-cal-jumph{display:flex;align-items:center;justify-content:space-between;gap:8px;padding:0 2px 5px}
.qf-cal-jumpi{text-align:left;padding:4px 7px!important;border-radius:4px}
.qf-cal-jumpi:hover{background:var(--qf-row-hover)!important}
.qf-cal-jumpi[aria-pressed="true"]{background:var(--qf-selection)!important;color:var(--qf-accent)!important}
.qf-cal-jumpf{font-size:11px;padding:5px 2px 0}
.qf-cal-mlogos{display:flex;flex-wrap:wrap;gap:3px;align-items:center;margin-top:auto;padding-top:3px}
`;
